import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ATTRIBUTE_TYPES, type AttributeRequest, type Locale } from '@shop/types';
import { Plus, Trash2 } from 'lucide-react';
import { type SyntheticEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../components/layout';
import { LocaleTabs } from '../components/locale-tabs';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { type Columns, DataTable } from '../components/ui/data-table';
import { Dialog } from '../components/ui/dialog';
import { Field } from '../components/ui/field';
import { Checkbox, Input, Select } from '../components/ui/input';
import { api, errorMessage } from '../lib/api';
import { attributesQuery, pickTranslation } from '../lib/queries';
import { useCan } from '../lib/session';
import type { Attribute } from '../lib/types';

const FLAGS = ['isFilterable', 'isSearchable', 'isVariantAxis', 'isLocalized'] as const;
const FLAG_LABEL = {
  isFilterable: 'attributes.filterable',
  isSearchable: 'attributes.searchable',
  isVariantAxis: 'attributes.variantAxis',
  isLocalized: 'attributes.localized',
} as const;

export function AttributesPage() {
  const { t, i18n } = useTranslation();
  const canWrite = useCan('catalog.write');
  const attributes = useQuery(attributesQuery);
  const [open, setOpen] = useState(false);

  const columns = useMemo<Columns<Attribute>>(
    () => [
      { id: 'code', header: t('common.code'), accessorFn: (a) => a.code },
      {
        id: 'name',
        header: t('common.name'),
        accessorFn: (a) => pickTranslation(a.names, i18n.language) ?? '',
      },
      {
        id: 'type',
        header: t('attributes.type'),
        accessorFn: (a) => a.type,
        cell: ({ row }) => t(`attributes.types.${row.original.type}`),
      },
      {
        id: 'flags',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="flex flex-wrap gap-1">
            {FLAGS.filter((f) => row.original[f]).map((f) => (
              <Badge key={f} variant="secondary">
                {t(FLAG_LABEL[f])}
              </Badge>
            ))}
          </span>
        ),
      },
      {
        id: 'options',
        header: t('attributes.options'),
        accessorFn: (a) => a.options.length,
        cell: ({ row }) =>
          row.original.options
            .map((o) => pickTranslation(o.labels, i18n.language) ?? o.code)
            .join(', '),
      },
    ],
    [t, i18n.language],
  );

  return (
    <>
      <PageHeader
        title={t('attributes.title')}
        actions={
          canWrite ? (
            <Button
              onClick={() => {
                setOpen(true);
              }}
            >
              <Plus />
              {t('attributes.new')}
            </Button>
          ) : null
        }
      />
      {attributes.error ? (
        <Alert variant="destructive">{errorMessage(attributes.error)}</Alert>
      ) : null}
      <DataTable
        columns={columns}
        data={attributes.data ?? []}
        loading={attributes.isPending}
        rowKey={(a) => a.id}
      />
      {open ? (
        <AttributeDialog
          onClose={() => {
            setOpen(false);
          }}
        />
      ) : null}
    </>
  );
}

function AttributeDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [locale, setLocale] = useState<Locale>('lv');
  const [code, setCode] = useState('');
  const [type, setType] = useState<AttributeRequest['type']>('text');
  const [unit, setUnit] = useState('');
  const [flags, setFlags] = useState<Record<(typeof FLAGS)[number], boolean>>({
    isFilterable: false,
    isSearchable: false,
    isVariantAxis: false,
    isLocalized: false,
  });
  const [names, setNames] = useState<Partial<Record<Locale, string>>>({});
  const [options, setOptions] = useState<
    { code: string; labels: Partial<Record<Locale, string>> }[]
  >([]);
  const [error, setError] = useState<string>();
  const hasOptions = type === 'enum' || type === 'multi_enum';

  const clean = (labels: Partial<Record<Locale, string>>) =>
    Object.fromEntries(Object.entries(labels).filter(([, v]) => v.trim()));

  const submit = async (e: SyntheticEvent) => {
    e.preventDefault();
    const body: AttributeRequest = {
      code,
      type,
      ...(unit.trim() ? { unit: unit.trim() } : {}),
      ...flags,
      names: clean(names),
      options: hasOptions
        ? options
            .filter((o) => o.code.trim())
            .map((o) => ({ code: o.code.trim(), labels: clean(o.labels) }))
        : [],
    };
    try {
      await api('/catalog/attributes', { method: 'POST', body });
      await queryClient.invalidateQueries({ queryKey: attributesQuery.queryKey });
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <Dialog open onClose={onClose} title={t('attributes.new')} className="max-w-2xl">
      <form onSubmit={(e) => void submit(e)} className="grid gap-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label={t('common.code')} hint={t('attributes.codeHint')}>
            {(id) => (
              <Input
                id={id}
                required
                pattern="[a-z][a-z0-9_]{1,63}"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                }}
              />
            )}
          </Field>
          <Field label={t('attributes.type')}>
            {(id) => (
              <Select
                id={id}
                value={type}
                onChange={(e) => {
                  setType(e.target.value as AttributeRequest['type']);
                }}
              >
                {ATTRIBUTE_TYPES.map((ty) => (
                  <option key={ty} value={ty}>
                    {t(`attributes.types.${ty}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('attributes.unit')}>
            {(id) => (
              <Input
                id={id}
                value={unit}
                onChange={(e) => {
                  setUnit(e.target.value);
                }}
              />
            )}
          </Field>
        </div>
        <div className="flex flex-wrap gap-4">
          {FLAGS.map((f) => (
            <label key={f} className="flex items-center gap-1.5 text-sm">
              <Checkbox
                checked={flags[f]}
                onChange={(e) => {
                  setFlags({ ...flags, [f]: e.target.checked });
                }}
              />
              {t(FLAG_LABEL[f])}
            </label>
          ))}
        </div>
        <LocaleTabs value={locale} onChange={setLocale} filled={(l) => !!names[l]?.trim()} />
        <Field label={t('common.name')}>
          {(id) => (
            <Input
              id={id}
              value={names[locale] ?? ''}
              onChange={(e) => {
                setNames({ ...names, [locale]: e.target.value });
              }}
            />
          )}
        </Field>
        {hasOptions ? (
          <fieldset className="grid gap-2">
            <legend className="mb-1 text-sm font-medium">
              {t('attributes.options')} ({locale.toUpperCase()})
            </legend>
            {options.map((o, i) => (
              <div key={i} className="grid grid-cols-[10rem_1fr_auto] gap-2">
                <Input
                  aria-label={t('attributes.optionCode')}
                  placeholder={t('attributes.optionCode')}
                  value={o.code}
                  onChange={(e) => {
                    setOptions(
                      options.map((x, j) => (j === i ? { ...x, code: e.target.value } : x)),
                    );
                  }}
                />
                <Input
                  aria-label={t('common.name')}
                  placeholder={t('common.name')}
                  value={o.labels[locale] ?? ''}
                  onChange={(e) => {
                    setOptions(
                      options.map((x, j) =>
                        j === i ? { ...x, labels: { ...x.labels, [locale]: e.target.value } } : x,
                      ),
                    );
                  }}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('common.remove')}
                  onClick={() => {
                    setOptions(options.filter((_, j) => j !== i));
                  }}
                >
                  <Trash2 />
                </Button>
              </div>
            ))}
            <div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setOptions([...options, { code: '', labels: {} }]);
                }}
              >
                {t('attributes.addOption')}
              </Button>
            </div>
          </fieldset>
        ) : null}
        {error ? <Alert variant="destructive">{error}</Alert> : null}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit">{t('common.create')}</Button>
        </div>
      </form>
    </Dialog>
  );
}
